import * as React from "react";
import { format, parseISO, isValid, startOfDay, endOfDay, subDays, startOfMonth, endOfMonth } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Button } from "../Button";
import { Calendar } from "../Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { PHONE_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import styles from "./DateRangePicker.module.css";

export interface DateRangePickerProps {
  /** From date (YYYY-MM-DD) or null */
  from: string | null;
  /** To date (YYYY-MM-DD) or null */
  to: string | null;
  /** Called when the range changes */
  onSelect: (range: { from: string | null; to: string | null }) => void;
  /** Placeholder when no range selected */
  placeholder?: string;
  className?: string;
}

function parseDate(s: string | null): Date | undefined {
  if (!s) return undefined;
  const d = parseISO(s);
  return isValid(d) ? d : undefined;
}

function formatDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

function withPtLabel(text: string): string {
  return text.endsWith("(PT)") ? text : `${text} (PT)`;
}

// Anchor "now" to the Pacific calendar day. The backend interprets bare YYYY-MM-DD
// filter strings as Pacific time (see packages/data/src/utils/dateFilters.ts),
// so the picker must too -- otherwise a user in another timezone clicks "Today"
// and sends a date the backend reads as a different day. Returning a Date whose
// local Y/M/D equals PT's Y/M/D lets date-fns and react-day-picker keep
// operating in their usual local-time mode without further conversion.
function ptToday(): Date {
  const now = new Date();
  return new Date(now.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
}

const PRESETS: { label: string; getValue: () => { from: Date; to: Date } }[] = [
  { label: "Today", getValue: () => ({ from: startOfDay(ptToday()), to: endOfDay(ptToday()) }) },
  { label: "Last 7 days", getValue: () => ({ from: startOfDay(subDays(ptToday(), 6)), to: endOfDay(ptToday()) }) },
  { label: "Last 30 days", getValue: () => ({ from: startOfDay(subDays(ptToday(), 29)), to: endOfDay(ptToday()) }) },
  { label: "This month", getValue: () => ({ from: startOfMonth(ptToday()), to: endOfMonth(ptToday()) }) },
];

export function DateRangePicker({ from, to, onSelect, placeholder = "Pick a date range", className }: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  // A phone shows one month. Two months do not fit its width, and the
  // arrows step one month in both layouts.
  const isPhone = useMediaQuery(PHONE_QUERY);

  const fromDate = parseDate(from);
  const toDate = parseDate(to);
  const range: DateRange | undefined =
    fromDate || toDate
      ? { from: fromDate ?? toDate ?? undefined, to: toDate ?? fromDate ?? undefined }
      : undefined;

  const handleSelect = (r: DateRange | undefined) => {
    if (!r) {
      onSelect({ from: null, to: null });
      return;
    }
    const fromStr = r.from ? formatDate(r.from) : null;
    const toStr = r.to ? formatDate(r.to) : null;
    onSelect({ from: fromStr, to: toStr });
  };

  const handlePreset = (preset: (typeof PRESETS)[number]) => {
    const { from: f, to: t } = preset.getValue();
    onSelect({ from: formatDate(f), to: formatDate(t) });
    setOpen(false);
  };

  const rangeText =
    from && to
      ? from === to
        ? from
        : `${from} – ${to}`
      : from
        ? `From ${from}`
        : to
          ? `To ${to}`
          : null;
  const displayText = withPtLabel(rangeText ?? placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="default"
          className={`${styles.trigger}${className ? ` ${className}` : ''}`}
        >
          <CalendarIcon className={styles.triggerIcon} />
          {displayText}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={styles.popover}>
        <div className={styles.dropdownBody}>
          <div className={styles.presets}>
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className={styles.presetButton}
                onClick={() => handlePreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <Calendar
            mode="range"
            defaultMonth={fromDate ?? toDate ?? ptToday()}
            today={ptToday()}
            selected={range}
            onSelect={handleSelect}
            numberOfMonths={isPhone ? 1 : 2}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
